/**
 * SupervisorWidget — personal "my work" tiles (CAPAs / PTW / observations /
 * team competence / expiring certs / priority tasks). Calls the existing
 * /dashboard/supervisor endpoint (per-user scope) directly.
 */
import React from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useDashboard } from '../context/DashboardContext';
import { useDashboardData } from '../hooks/useDashboardData';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function Tile({ label, value, accent = 'text-gray-900', to, onNavigate }) {
  const clickable = typeof to === 'string' && typeof onNavigate === 'function';
  const go = clickable ? () => onNavigate(to) : undefined;
  return (
    <div
      className={`rounded-md border border-gray-200 bg-white p-3 text-center ${
        clickable ? 'cursor-pointer transition-shadow hover:border-gray-300 hover:shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-400/50' : ''
      }`}
      onClick={go}
      onKeyDown={clickable ? (e) => (e.key === 'Enter' || e.key === ' ') && go() : undefined}
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? 0 : undefined}
      title={clickable ? `Open ${label}` : undefined}
    >
      <div className={`text-2xl font-bold ${accent}`}>{value ?? 0}</div>
      <div className="mt-1 text-xs text-gray-500">{label}</div>
    </div>
  );
}

export default function SupervisorWidget() {
  const { refreshMs } = useDashboard();
  const navigate = useNavigate();
  const { data, loading, error } = useDashboardData(
    (signal) => axios.get(`${API}/dashboard/supervisor`, { signal }).then((r) => r.data),
    [],
    { refreshMs },
  );

  const tasks = data?.priority_tasks || [];

  return (
    <div className="rounded-xl border border-gray-200/80 bg-white p-4 shadow-sm transition-shadow duration-200 hover:shadow-md">
      <h3 className="mb-3 text-[11px] font-semibold uppercase tracking-wide text-gray-400">My Work</h3>
      {loading && <div className="h-24 animate-pulse rounded bg-gray-100" />}
      {!loading && error && <div className="text-sm text-red-600">Unavailable</div>}
      {!loading && !error && (
        <>
          {/* Personal — scoped to the logged-in user (assigned to / reported by / applicant = me). */}
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
            <Tile label="My Open CAPAs" value={data?.my_open_capas} accent="text-orange-600" to="/capa" onNavigate={navigate} />
            <Tile label="My Overdue CAPAs" value={data?.my_overdue_capas} accent="text-red-600" to="/capa" onNavigate={navigate} />
            <Tile label="My Observations (wk)" value={data?.my_observations_this_week} accent="text-green-600" to="/observations" onNavigate={navigate} />
            <Tile label="My Extended PTW" value={data?.my_ptw_extended} accent="text-amber-600" to="/ptw" onNavigate={navigate} />
            <Tile label="Pending My Approval" value={data?.my_pending_ptw_approvals} accent="text-rose-600" to="/ptw" onNavigate={navigate} />
          </div>

          {/* Site-wide figures for the user's effective site (not personal). */}
          <div className="mb-2 mt-4 text-[11px] font-semibold uppercase tracking-wide text-gray-400">At My Site</div>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
            <Tile label="Active PTW (site)" value={data?.my_active_ptws} accent="text-blue-600" to="/ptw" onNavigate={navigate} />
            <Tile label="Certs Expiring · 30d (site)" value={data?.team_certifications_expiring} accent="text-purple-600" to="/training/certifications" onNavigate={navigate} />
          </div>

          <div className="mt-4">
            <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">Today's Priority Tasks</div>
            {tasks.length === 0 ? (
              <p className="py-2 text-center text-sm text-gray-400">No priority tasks</p>
            ) : (
              <ul className="divide-y divide-gray-100">
                {tasks.slice(0, 6).map((t, i) => (
                  <li
                    key={t.id || i}
                    className={`flex items-center justify-between py-2 text-sm ${t.link ? 'cursor-pointer hover:text-blue-700' : ''}`}
                    onClick={t.link ? () => navigate(t.link) : undefined}
                  >
                    <span className="truncate">{t.title || t.description || 'Task'}</span>
                    {t.due_date && <span className="ml-2 shrink-0 text-xs text-gray-400">{String(t.due_date).slice(0, 10)}</span>}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </>
      )}
    </div>
  );
}
